import {
  Timestamp,
  Unsubscribe,
  collection,
  getDocs,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * The member's side of `conversations`.
 *
 * This shape was previously declared twice — inline in `MessagesPage` and
 * implicitly in `MemberRail`'s reducer — and the dashboard would have made it
 * three. One declaration, one query, one sort order.
 *
 * Members can read their own conversation docs and update a narrow field set
 * (`firestore.rules` conversations block). `priority` is deliberately NOT in
 * that set: it is written server-side when a clinician escalates, so the
 * dashboard can trust it as an urgency signal rather than something the client
 * could set for itself. Nothing writes it yet — see the P2 note in the plan —
 * so `hasUrgent` is currently always false, and that is the intended resting
 * state rather than a bug.
 */
export interface MemberConversation {
  id: string;
  clinicianName?: string;
  clinicianEmail?: string;
  patientUid?: string;
  patientEmail?: string;
  patientName?: string;
  status?: string;
  priority?: 'normal' | 'urgent' | null;
  appointmentTime?: Timestamp | null;
  meetingLink?: string;
  lastMessageAt?: Timestamp | null;
  lastMessagePreview?: string;
  unreadByPatient?: number;
}

function toConversations(
  docs: { id: string; data: () => Record<string, unknown> }[],
): MemberConversation[] {
  return docs
    .map((document) => ({ id: document.id, ...document.data() } as MemberConversation))
    .sort((left, right) => {
      const leftTime = left.lastMessageAt?.toMillis?.() || 0;
      const rightTime = right.lastMessageAt?.toMillis?.() || 0;
      return rightTime - leftTime;
    });
}

function memberConversationQuery(uid: string) {
  return query(collection(db, 'conversations'), where('patientUid', '==', uid));
}

/**
 * Live subscription, newest-message-first. Unread counts are the one thing on
 * the member surfaces that changes while a page is open, which is why this is a
 * listener rather than a one-shot read.
 */
export function subscribeMemberConversations(
  uid: string,
  onChange: (conversations: MemberConversation[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  return onSnapshot(
    memberConversationQuery(uid),
    (snapshot) => onChange(toConversations(snapshot.docs)),
    (error) => onError?.(error),
  );
}

export async function listMemberConversations(uid: string): Promise<MemberConversation[]> {
  const snapshot = await getDocs(memberConversationQuery(uid));
  return toConversations(snapshot.docs);
}

export interface MemberUnreadSummary {
  total: number;
  latest: MemberConversation | null;
  hasUrgent: boolean;
}

export function summariseUnread(conversations: MemberConversation[]): MemberUnreadSummary {
  const unread = conversations.filter((conversation) => (conversation.unreadByPatient || 0) > 0);
  return {
    total: conversations.reduce((sum, conversation) => sum + (conversation.unreadByPatient || 0), 0),
    // Already sorted newest-first, so the first unread thread is the latest one.
    latest: unread[0] || null,
    hasUrgent: unread.some((conversation) => conversation.priority === 'urgent'),
  };
}
